import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { ScrollbarComponent } from '@atoms/scrollbar/scrollbar.component';
import { SizesService } from '@services/sizes.service';
import { SsrService } from '@services/ssr.service';

@Component({
  selector: 'app-dropdown-content',
  imports: [NgClass, NgTemplateOutlet, ScrollbarComponent],
  templateUrl: './dropdown-content.component.html',
  styleUrls: ['./dropdown-content.component.scss'],
})
export class DropdownContentComponent implements AfterViewInit, OnChanges, OnDestroy {
  public sizesService = inject(SizesService);
  private ssrService = inject(SsrService);
  @Input() button?: boolean;
  @Input() height?: number;
  @Output() scrollChange: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('dropdownContent') dropdownContent?: ElementRef;
  public resizeObserver?: ResizeObserver;
  public maxHeight: number = 484;
  public heightLimit: number = 484;

  @HostListener('document:click', ['$event.target']) clickout(event: HTMLElement): void {
    const activeEl: HTMLElement | null = this.dropdownContent?.nativeElement.closest('.dropdown__main--active');
    if (activeEl) this.setMaxHeight();
  }

  @HostListener('window:resize') onResize(): void {
    this.setMaxHeight();
  }

  scrollChanged(event: number): void {
    this.scrollChange.emit(event);
    const header: HTMLElement = this.dropdownContent?.nativeElement.closest('app-dropdown')?.querySelector('.dropdown__header');
    if (!header) return;
    if (event > 1) {
      header.classList.add('dropdown__header--active');
    } else {
      header.classList.remove('dropdown__header--active');
    }
  }

  init(): void {
    const header: HTMLElement = this.dropdownContent?.nativeElement.closest('app-dropdown')?.querySelector('.dropdown__header');
    if (!this.dropdownContent) return;
    this.dropdownContent.nativeElement.style.paddingTop = header ? '0' : '';
    const headerSearch: HTMLElement | null = header?.querySelector('.search-input__input');
    headerSearch?.focus();
  }

  setMaxHeight(): void {
    if (this.ssrService.isServer() || !this.dropdownContent) return;
    const windowHeight: number = window.innerHeight;
    const headerHeight: number = 112;
    const dropdownHeader: HTMLElement | null = this.dropdownContent.nativeElement.closest('.dropdown__main')?.querySelector('.dropdown__header');
    const dropdownHeaderHeight: number = dropdownHeader ? dropdownHeader.getBoundingClientRect().height : 0;

    const absolute = this.dropdownContent.nativeElement.closest('.dropdown__main--absolute');
    if (absolute) {
      const absoluteTop: number = absolute.style.top ? parseInt(absolute.style.top) : 0;
      this.maxHeight = windowHeight - absoluteTop - dropdownHeaderHeight - 24;
    } else {
      this.maxHeight = windowHeight - headerHeight - dropdownHeaderHeight - 24;
    }

    if (this.maxHeight > this.heightLimit) this.maxHeight = this.heightLimit;
    if (this.height && this.height < this.maxHeight) this.maxHeight = this.height;
    if (this.maxHeight < 300) this.maxHeight = 300;
  }

  ngAfterViewInit(): void {
    if (this.ssrService.isServer() || !this.dropdownContent) return;
    setTimeout(() => this.setMaxHeight(), 0);
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.init();
      });
    });
    this.resizeObserver.observe(this.dropdownContent.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['height'] && !changes['height'].firstChange) {
      this.setMaxHeight();
    }
  }

  ngOnDestroy(): void {
    if (this.ssrService.isServer()) return;
    this.resizeObserver?.disconnect();
  }
}
